import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { origensEnum } from '../../../shared/constantes/origensEnum';
import IntegracaoService from '../../../services/integracaoService';
import {Originador} from '../../../shared/model/originador';

interface IntegracaoOrigemModalProps {
  show: boolean;
  onHide: () => void;
  onSave: (origemConfig: any) => void;
  definicoes: any[];
  origemEdicao?: any | null;
  listaOrigensExistentes: any[];
}

const IntegracaoOrigemModal: React.FC<IntegracaoOrigemModalProps> = ({
  show,
  onHide,
  onSave,
  definicoes,
  origemEdicao,
  listaOrigensExistentes
}) => {
  const service = new IntegracaoService();
  
  const [tipoOrigem, setTipoOrigem] = useState('');
  const [idOrigemSelecionada, setIdOrigemSelecionada] = useState('');
  const [nomeOrigemSelecionada, setNomeOrigemSelecionada] = useState('');
  const [valoresCredenciais, setValoresCredenciais] = useState<{[key: string]: string}>({});
  const [visibilidadeSenhas, setVisibilidadeSenhas] = useState<{ [key: string]: boolean }>({});
  
  const [listaOpcoes, setListaOpcoes] = useState<any[]>([]);
  const [loadingOpcoes, setLoadingOpcoes] = useState(false);
  const [erro, setErro] = useState('');

  useEffect(() => {
    if (show) {
      setErro('');
      const credenciaisPadrao: { [key: string]: string } = definicoes.some(d => d.integracao === 'SERASA' && d.nomeParametro === 'METODO_AUTENTICACAO')
        ? { METODO_AUTENTICACAO: 'v1' } : {};

      if (origemEdicao) {
        setTipoOrigem(origemEdicao.tipoOrigemDesc);
        setIdOrigemSelecionada(String(origemEdicao.idOrigem));
        setNomeOrigemSelecionada(origemEdicao.nomeOrigem);
        setValoresCredenciais({ ...credenciaisPadrao, ...origemEdicao.credenciais });
      } else {
        setTipoOrigem('');
        setIdOrigemSelecionada('');
        setNomeOrigemSelecionada('');
        setValoresCredenciais(credenciaisPadrao);
      }
    }
  }, [show, origemEdicao, definicoes]);

  useEffect(() => {
    const carregar = async () => {
      setListaOpcoes([]);
      setIdOrigemSelecionada('');
      if (!tipoOrigem || tipoOrigem === 'TODAS') return;

      setLoadingOpcoes(true);
      try {
        let response;
        if (tipoOrigem === 'MATRIZ') {
          response = await service.listarTodasMatrizesAtivas();
        } else if (tipoOrigem === 'ORIGINADOR') {
          response = await service.listarTodosOriginadoresAtivos();
        }
        
        if (response && response.data) {
          setListaOpcoes(response.data);
        }
      } catch (e) {
        setErro('Erro ao carregar lista de origens.');
      } finally {
        setLoadingOpcoes(false);
      }
    };
    
    if (show && origemEdicao && origemEdicao.tipoOrigemDesc === tipoOrigem && listaOpcoes.length === 0) {
        carregar().then(() => setIdOrigemSelecionada(String(origemEdicao.idOrigem)));
    } else {
        carregar();
    }
  }, [tipoOrigem]);

  const credenciaisVisiveis = definicoes
    .filter(d => d.tipoParametro === 'CREDENCIAL')
    .filter(d => !(d.integracao === 'SERASA' && d.nomeParametro === 'CLIENT_ID')
      || valoresCredenciais.METODO_AUTENTICACAO === 'v2')
    .sort((a, b) => Number(b.nomeParametro === 'METODO_AUTENTICACAO')
      - Number(a.nomeParametro === 'METODO_AUTENTICACAO'));

  const handleSave = () => {
    if (!tipoOrigem) { setErro('Selecione um tipo.'); return; }
    if (tipoOrigem !== 'TODAS' && !idOrigemSelecionada) { setErro('Selecione a origem.'); return; }
    
    const jaExiste = listaOrigensExistentes.some(o => 
      o.uuid !== (origemEdicao?.uuid) && 
      ((o.tipoOrigemDesc === 'TODAS') || (tipoOrigem === 'TODAS') || (o.tipoOrigemDesc === tipoOrigem && String(o.idOrigem) === String(idOrigemSelecionada)))
    );

    if (jaExiste) {
      setErro(tipoOrigem === 'TODAS' ? 'Já existe configuração. "Todas" deve ser única.' : 'Esta origem já está adicionada.');
      return;
    }

    const credsNecessarias = credenciaisVisiveis;
    const credsFaltantes = credsNecessarias.filter(d => !valoresCredenciais[d.nomeParametro]);
    if (credsFaltantes.length > 0) {
      setErro(`Preencha: ${credsFaltantes.map(c => c.nomeParametro).join(', ')}`);
      return;
    }

    const nomeFinal = tipoOrigem === 'TODAS' 
        ? 'Todas as Origens' 
        : listaOpcoes.find(o => String(o.id) === String(idOrigemSelecionada))?.nome || 'Desconhecida';

    let codigoTipo = 0;
    if (tipoOrigem === 'MATRIZ') codigoTipo = 1;
    if (tipoOrigem === 'ORIGINADOR') codigoTipo = 6;

    const config = {
      uuid: origemEdicao?.uuid || crypto.randomUUID(),
      tipoOrigemDesc: tipoOrigem,
      tipoOrigemCod: codigoTipo, 
      idOrigem: tipoOrigem === 'TODAS' ? 0 : idOrigemSelecionada,
      nomeOrigem: nomeFinal,
      credenciais: valoresCredenciais
    };

    onSave(config);
    onHide();
  };

  const togglePass = (key: string) => {
    setVisibilidadeSenhas(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{origemEdicao ? 'Editar Origem' : 'Nova Origem'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {erro && <div className="alert alert-danger">{erro}</div>}
        
        <div className="row mb-3">
          <div className="col-md-6">
            <label>Tipo de Origem</label>
            <select className="form-select" value={tipoOrigem} onChange={e => setTipoOrigem(e.target.value)} disabled={!!origemEdicao}>
              <option value="">Selecione...</option>
              <option value="TODAS">Todas</option>
              <option value="MATRIZ">Matriz</option>
              <option value="ORIGINADOR">{Originador.DESCRITIVO_GERAL}</option>
            </select>
          </div>
          <div className="col-md-6">
            <label>Origem</label>
            {tipoOrigem === 'TODAS' ? (
               <input type="text" className="form-control" value="Aplicado a todas as origens" disabled />
            ) : (
               <select 
                 className="form-select" 
                 value={idOrigemSelecionada} 
                 onChange={e => setIdOrigemSelecionada(e.target.value)}
                 disabled={!tipoOrigem || loadingOpcoes}
                >
                 <option value="">{loadingOpcoes ? 'Carregando...' : 'Selecione...'}</option>
                 {listaOpcoes.map(o => <option key={o.id} value={o.id}>{o.nome}</option>)}
               </select>
            )}
          </div>
        </div>

        <hr />
        <h6>Credenciais de Acesso</h6>
        <p className="text-muted small">Após aplicar, clique em “Salvar Alterações” na tela da integração para gravar as credenciais.</p>
        <div className="row">
          {credenciaisVisiveis.map(d => {
            if (d.integracao === 'SERASA' && d.nomeParametro === 'METODO_AUTENTICACAO') {
              return (
                <div className="col-md-6 mb-2" key={d.id}>
                  <label htmlFor="serasa-metodo-autenticacao">Método de autenticação</label>
                  <select
                    id="serasa-metodo-autenticacao"
                    className="form-select"
                    value={valoresCredenciais[d.nomeParametro] || 'v1'}
                    onChange={e => setValoresCredenciais({ ...valoresCredenciais, [d.nomeParametro]: e.target.value })}
                  >
                    <option value="v1">v1</option>
                    <option value="v2">v2</option>
                  </select>
                </div>
              );
            }
            const isPass = d.nomeParametro.toLowerCase().includes('senha') || d.nomeParametro.toLowerCase().includes('api') || d.nomeParametro.toLowerCase().includes('clientsecret');
            const showPass = visibilidadeSenhas[d.nomeParametro];
            
            return (
              <div className="col-md-6 mb-2" key={d.id}>
                <label htmlFor={`credencial-${d.id}`}>{d.integracao === 'SERASA'
                  ? ({ CLIENT_ID: 'ClientID', USUARIO: 'Usuário', SENHA: 'Senha' } as Record<string, string>)[d.nomeParametro] || d.nomeParametro
                  : d.nomeParametro}</label>
                <div className="input-group">
                  <input 
                    id={`credencial-${d.id}`}
                    type={isPass && !showPass ? 'password' : 'text'} 
                    className="form-control"
                    value={valoresCredenciais[d.nomeParametro] || ''}
                    onChange={e => setValoresCredenciais({...valoresCredenciais, [d.nomeParametro]: e.target.value})}
                  />
                  {isPass && (
                    <button className="btn btn-outline-secondary" type="button" onClick={() => togglePass(d.nomeParametro)}>
                      👁️
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          {definicoes.filter(d => d.tipoParametro === 'CREDENCIAL').length === 0 && (
            <div className="text-muted">Nenhuma credencial requerida para esta integração.</div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Cancelar</Button>
        <Button variant="success" onClick={handleSave}>Aplicar à integração</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default IntegracaoOrigemModal;